import { Component, Input, forwardRef, ContentChild, TemplateRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { RadioOption } from '../primitives.types';

@Component({
  selector: 'kt-radio-group',
  templateUrl: './radio-group.component.html',
  styleUrls: ['./radio-group.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RadioGroupComponent), multi: true }]
})
export class RadioGroupComponent implements ControlValueAccessor {
  @Input() options: RadioOption[] = [];
  @Input() label = '';
  /** When set, clamps the label to this many lines and reserves that height, so labels of
   * differing length stay vertically aligned across a row of fields. Unset = natural height. */
  @Input() labelLines: number = null;
  @Input() orientation: 'vertical' | 'horizontal' = 'vertical';
  @Input() disabled = false;
  @Input() required = false;
  @Input() errorMessage = '';
  @Input() hint = '';

  /**
   * Optional per-option content template, projected by the consumer as
   * `<ng-template #optionTemplate let-option>...</ng-template>` inside `<kt-radio-group>`.
   * When supplied, it replaces the default `label`/`hint` text for every option (context is
   * the full `RadioOption`, so use `option.data` for a custom payload) — kt-radio-group still
   * owns the underlying `mat-radio-group`/`mat-radio-button` wiring, selection state, and
   * keyboard nav. When absent (the default for every existing usage), rendering is 100%
   * unchanged from the plain label/hint behavior.
   */
  @ContentChild('optionTemplate') optionTemplate: TemplateRef<any> | null = null;

  value: any = null;

  onChange = (_: any) => { };
  onTouched = () => { };

  onRadioChange(optValue: any) {
    this.value = optValue;
    this.onChange(this.value);
    this.onTouched();
  }

  /** Keys *ngFor by option value (not array/object identity) so a consumer passing a freshly
   * computed `options` array (e.g. from a getter) each change-detection cycle doesn't cause
   * Angular to tear down and recreate every radio — which would otherwise interrupt clicks
   * and drop focus/selection mid-interaction. */
  trackByValue(index: number, opt: RadioOption) {
    return opt.value;
  }

  writeValue(value: any) { this.value = value ?? null; }
  registerOnChange(fn: any) { this.onChange = fn; }
  registerOnTouched(fn: any) { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean) { this.disabled = isDisabled; }
}
